/**
 * ノード 1 件を、その型の詳細画面へのリンクとして描く。
 *
 * 行き先の判断は `resolveSpecNodeLink` が持ち、この部品はルート定数との対応だけを持つ。
 * 行き先を持たない型は `<span>` に倒す。押しても何も起きないリンクを描くと、利用者は
 * 「壊れている」のか「行き先が無い」のかを判断できない。
 *
 * **PJ モードの UserStory だけは行き先そのものが変わる。** 検索条件で PJ を引き継ぐ他の型と
 * 違い、US の PJ つきの姿は `PJ_FLOW_ROUTE` が受け持ち、PJ はパスの親に置かれる。
 * この判断をここへ置くことで、PJ サマリーからも業務ツリーからも同じ行き先になる。
 *
 * 見た目は `className` で呼び出し側が決める。逆引き一覧の 1 行・カード全体・本文中の
 * 語のいずれにも同じ判断を効かせたいので、枠と余白は持たない。
 *
 * PJ モードを引き継ぐ検索条件は `pjLinkSearch` から引く。同じ判断を一覧の行と
 * この部品の 2 箇所で持つと、片方だけ条件を変えたときに導線によって PJ が落ちる。
 *
 * **いま見ている PJ は呼び出し側から受け取らず、現在地から読む。** 任意の prop で受けていた
 * ときは 9 箇所の呼び出しのうち 7 箇所が渡しておらず、業務詳細・画面詳細・用語詳細・マスター
 * 詳細から先のリンクが PJ を落としていた。落ちたことは画面に出ないので、PJ を適用した後の姿を
 * 読んでいるつもりでマスターを読むことになる。渡し忘れは型でも lint でも落ちないので、
 * 「呼び出し側が渡す」形にしている限り同じ取りこぼしがまた起きる。
 *
 * **PJ を跨ぐ画面だけは行ごとに PJ が違うので `SpecNodeLinkInPj` を使う。** 承認一覧は現在地に
 * PJ を持たず、行ごとに別の PJ の提案を並べる。現在地から読むと PJ が無いのでマスターの詳細が
 * 開き、その PJ で新設した用語は「見つかりません」になっていた。こちらは PJ 番号を**必須**で
 * 受けるので、渡し忘れは型で落ちる。行き先の判断そのものは両方で `renderSpecNodeLink` を共有する。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ノード 1 件を、型に応じた詳細画面へのリンク（無ければ素の文字列）として描く。
 * @graph-connects resolveSpecNodeLink [calls] ノード型から行き先を決める
 * @graph-connects useCurrentPj [calls] いま見ている PJ 番号
 * @graph-connects pjLinkSearch [calls] PJ モードを引き継ぐリンクの検索条件
 * @graph-connects isPjMode [calls] PJ モードかどうかの判定
 */
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useCurrentPj } from '../../hooks/usePjMode.js';
import { resolveSpecNodeLink } from '../../lib/spec-node-links.js';
import type { SpecNodeTypeValue } from '../../lib/spec-node-types.js';
import { isPjMode } from '../../lib/pj-mode.js';
import {
  BUSINESS_DETAIL_ROUTE,
  FLOW_ROUTE,
  MASTER_DETAIL_ROUTE,
  PJ_FLOW_ROUTE,
  RULE_ROUTE,
  SCREEN_DETAIL_ROUTE,
  TERM_DETAIL_ROUTE,
} from '../../routes/route-paths.js';
import { pjLinkSearch } from '../../routes/pj-link.js';

/** `SpecNodeLink` の props。 */
export interface SpecNodeLinkProps {
  /** ノード型。 */
  nodeType: SpecNodeTypeValue;
  /** ノード ID。パスパラメータへそのまま渡す。 */
  nodeId: string;
  /** リンク（または素の文字列）に付ける class。 */
  className?: string;
  /** 表示する中身。 */
  children: ReactNode;
}

/** `SpecNodeLinkInPj` の props。 */
export interface SpecNodeLinkInPjProps extends SpecNodeLinkProps {
  /**
   * リンク先で重ねる PJ 番号。現在地ではなく、行が属する PJ を渡す。
   *
   * **master の姿へ送りたいときは `undefined` を明示的に渡す。** 任意（`?`）にしないのは、
   * 渡し忘れを型で落とし続けるためである — 省略できる形にした途端、PJ を渡すべき導線が黙って
   * master へ落ちる事故が型でも lint でも拾えなくなる。`undefined` を書くのは「PJ を持たない
   * 行き先を選んだ」という判断の表明であって、書き忘れとは別物である（PJ が消す用語は重ねた
   * 姿に居ないので、そこへ送ると必ず「見つかりません」になる）。
   */
  pjNumber: string | undefined;
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ノード 1 件を、いま見ている PJ を引き継いだまま型に応じた詳細画面へのリンクとして描く。
 * @graph-connects useCurrentPj [calls] いま見ている PJ 番号
 * @graph-connects renderSpecNodeLink [calls] 型と PJ から行き先を決めて描く
 */
export function SpecNodeLink(props: SpecNodeLinkProps) {
  const pj = useCurrentPj();
  return renderSpecNodeLink(props, pj);
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ を跨ぐ一覧の 1 行から、その行が属する PJ を重ねた詳細画面へのリンクを描く。
 * @graph-connects renderSpecNodeLink [calls] 型と PJ から行き先を決めて描く
 */
export function SpecNodeLinkInPj({ pjNumber, ...props }: SpecNodeLinkInPjProps) {
  return renderSpecNodeLink(props, pjNumber);
}

/**
 * 型と PJ 番号から行き先を決めて描く。PJ をどこから得るかは呼び出す部品が決める。
 *
 * @param props ノード型・ID・見た目・中身
 * @param pj 重ねる PJ 番号。PJ モードでなければ undefined
 * @returns リンク。行き先の無い型は素の文字列
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ノード型と PJ 番号から詳細画面の行き先を決め、リンク（無ければ素の文字列）を描く。
 * @graph-connects resolveSpecNodeLink [calls] ノード型から行き先を決める
 * @graph-connects pjLinkSearch [calls] PJ モードを引き継ぐリンクの検索条件
 * @graph-connects isPjMode [calls] PJ モードかどうかの判定
 */
function renderSpecNodeLink(
  { nodeType, nodeId, className, children }: SpecNodeLinkProps,
  pj: string | undefined,
) {
  const target = resolveSpecNodeLink(nodeType, nodeId);
  const search = pjLinkSearch(pj);

  if (target.kind === 'user-story') {
    /*
     * PJ を選んでいるときの US の行き先は、US 詳細ではなく PJ から見たフローである。US 詳細は
     * 検索条件の PJ を読まない（PJ つきのフローは `PJ_FLOW_ROUTE` が受け持ち、PJ はパスの
     * 親に置かれる）ので、`?pj=` を付けて送っても PJ は黙って外れ、マスターのフローが開く。
     * 外れたことは画面に出ないので、利用者は PJ を適用した姿を見ていると思ったまま読む。
     */
    if (isPjMode(pj)) {
      return (
        <Link
          to={PJ_FLOW_ROUTE}
          params={{ pjNumber: pj, flowId: target.usId }}
          className={className}
        >
          {children}
        </Link>
      );
    }
    /*
     * PJ を渡さない。ここに来るのは PJ を選んでいないときだけで、上の分岐が PJ モードを
     * 引き取っている。渡しても読まれない条件を付けると、URL の見た目だけが PJ を主張する。
     */
    return (
      <Link to={FLOW_ROUTE} params={{ flowId: target.usId }} className={className}>
        {children}
      </Link>
    );
  }
  if (target.kind === 'business') {
    return (
      <Link
        to={BUSINESS_DETAIL_ROUTE}
        params={{ businessId: target.businessId }}
        {...search}
        className={className}
      >
        {children}
      </Link>
    );
  }
  if (target.kind === 'rule') {
    return (
      <Link to={RULE_ROUTE} params={{ ruleId: target.ruleId }} {...search} className={className}>
        {children}
      </Link>
    );
  }
  if (target.kind === 'screen') {
    return (
      <Link
        to={SCREEN_DETAIL_ROUTE}
        params={{ screenId: target.screenId }}
        {...search}
        className={className}
      >
        {children}
      </Link>
    );
  }
  if (target.kind === 'term') {
    return (
      <Link
        to={TERM_DETAIL_ROUTE}
        params={{ termId: target.termId }}
        {...search}
        className={className}
      >
        {children}
      </Link>
    );
  }
  if (target.kind === 'master') {
    return (
      <Link
        to={MASTER_DETAIL_ROUTE}
        params={{ type: target.masterType, id: target.masterId }}
        {...search}
        className={className}
      >
        {children}
      </Link>
    );
  }

  return <span className={className}>{children}</span>;
}
